import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, AlertTriangle, X, Pencil, Wifi, WifiOff, Check } from 'lucide-react';
import { User, Quote } from '../types';
import { Enquiry, fetchEnquiries, createEnquiry, updateEnquiry, isLifecycleConnected, createBooking } from '../utils/lifecycleApi';
import { loadFromStorage } from '../utils/helpers';
import { ALL_PRODUCTS, ServiceKey } from '../data/productsData';

// Enquiry Tracker's service tabs use different display names than the rate
// catalog's ServiceKey — map the ones that exist in the catalog. Anniversary
// shares the catalog's "engagement" pricing; Any Event has no catalog entry
// at all (custom/ad-hoc), so it falls back to free text.
const SERVICE_TO_CATALOG_KEY: Record<string, ServiceKey | undefined> = {
  'Wedding': 'wedding', 'Pre-Wedding': 'pre_wedding', 'Engagement': 'engagement',
  'Anniversary': 'engagement', 'Birthday': 'birthday', 'Office Event': 'office_event', 'Any Event': undefined,
};

interface EnquiryTrackerProps { user: User; onBuildQuote?: (enquiry: Enquiry) => void; }

const SERVICES = ['Wedding','Pre-Wedding','Engagement','Anniversary','Birthday','Office Event','Any Event'];

const CONTACT_VIA_OPTIONS = ['Phone Call', 'Walk-in', 'Email', 'Social Media', 'Website', 'JustDial', 'Known Reference', 'Outside Meeting'];
const FOLLOW_UP_STATUS_OPTIONS = ['Pending', 'Contacted', 'Awaiting Customer Response', 'Scheduled Meeting', 'Not Interested', 'Not Applicable'];
const OVERALL_STATUS_OPTIONS = ['Open', 'Confirmed', 'Lost', 'On Hold'];

const STATUS_BADGE: Record<string, string> = {
  Open: 'badge-blue', Confirmed: 'badge-teal', Lost: 'badge-red', 'On Hold': 'badge-amber',
};

const emptyForm = {
  customer_name: '', contact_number: '', email: '', contact_via: CONTACT_VIA_OPTIONS[0],
  event_dates: '', location_venue: '', suggested_package: '',
  follow_up_date: '', follow_up_status: FOLLOW_UP_STATUS_OPTIONS[0],
  overall_status: OVERALL_STATUS_OPTIONS[0], comments: '',
};

export default function EnquiryTracker({ user, onBuildQuote }: EnquiryTrackerProps) {
  const [connected] = useState(isLifecycleConnected());
  const [activeService, setActiveService] = useState('Wedding');
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Enquiry | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Feature #3 (frontend half): searchable Rate Catalog picker for Suggested
  // & Shortlisted Package, in place of typing it freehand. Still saves into
  // the same suggested_package text field — becomes a clean swap to a real
  // structured reference once the Phase 2 migration adds that column.
  const [showPackagePicker, setShowPackagePicker] = useState(false);
  const catalogKey = SERVICE_TO_CATALOG_KEY[activeService];
  const catalogProducts = catalogKey ? ALL_PRODUCTS.filter(p => p.serviceKey === catalogKey) : [];
  const matchingProducts = form.suggested_package.trim().length > 0
    ? catalogProducts.filter(p => p.productName.toLowerCase().includes(form.suggested_package.toLowerCase())).slice(0, 30)
    : catalogProducts.slice(0, 30);
  const [bookingActionId, setBookingActionId] = useState<number | null>(null);
  const [bookingCreated, setBookingCreated] = useState<Record<number, string>>({});
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [pendingEnquiryId, setPendingEnquiryId] = useState<number | null>(null);
  const [quoteRefStudio, setQuoteRefStudio] = useState('');
  const [invoiceRefStudio, setInvoiceRefStudio] = useState('');
  const [linkedQuoteId, setLinkedQuoteId] = useState('');
  // Manual acknowledgement used only on the no-linked-quote path, where there's
  // no stored amount/advanceReceived to check against — see advanceOk below.
  const [manualAdvanceConfirmed, setManualAdvanceConfirmed] = useState(false);
  const [autoLinkedNote, setAutoLinkedNote] = useState(false);
  // Load planner quotes for linking — both quotations and invoices are linkable
  const plannerQuotes: Quote[] = loadFromStorage<Quote[]>('ss_quotes', []);
  const eligibleQuotes = plannerQuotes;
  const linkedQuote = linkedQuoteId ? plannerQuotes.find(q => q.id === linkedQuoteId) : null;

  // Gate: SOP rule is that advance actually received — not invoice creation —
  // is what allows a booking/lifecycle to be created. When a quote is linked
  // we can check its own advanceReceived flag directly (set via "Confirm
  // Advance Received" in Quote History/Dashboard). When no quote is linked,
  // there's nothing in the system to check, so we require an explicit
  // acknowledgement instead of silently allowing the booking through.
  const advanceOk = linkedQuoteId ? !!linkedQuote?.advanceReceived : manualAdvanceConfirmed;

  const openBookingModal = (enquiryId: number) => {
    setPendingEnquiryId(enquiryId);
    setQuoteRefStudio('');
    setInvoiceRefStudio('');
    setManualAdvanceConfirmed(false);
    setError('');
    // Feature #1: if this enquiry was already linked to a quote from the
    // Quote Quick Look side (freehand — enquiry and quote made separately),
    // pick that quote up automatically instead of making them find it again.
    let autoLinkedQuoteId = '';
    try {
      const links = JSON.parse(localStorage.getItem('ss_quote_enquiry_links') || '{}');
      const match = Object.entries(links).find(([, v]: [string, any]) => v.enquiryId === enquiryId);
      if (match) autoLinkedQuoteId = match[0];
    } catch {}
    setLinkedQuoteId(autoLinkedQuoteId);
    if (autoLinkedQuoteId) handleLinkQuote(autoLinkedQuoteId);
    setAutoLinkedNote(!!autoLinkedQuoteId);
    setShowBookingModal(true);
  };

  const handleLinkQuote = (quoteId: string) => {
    setLinkedQuoteId(quoteId);
    setManualAdvanceConfirmed(false);
    if (!quoteId) { setQuoteRefStudio(''); setInvoiceRefStudio(''); return; }
    const q = plannerQuotes.find(q => q.id === quoteId);
    if (!q) return;
    setQuoteRefStudio(q.quoteNumber || '');
    setInvoiceRefStudio((q as any).invoiceNumber || '');
  };

  const handleCreateBooking = async () => {
    if (!pendingEnquiryId || !advanceOk) return;
    setBookingActionId(pendingEnquiryId);
    setError('');
    try {
      const booking = await createBooking(pendingEnquiryId, quoteRefStudio||undefined, invoiceRefStudio||undefined);
      setBookingCreated(prev => ({ ...prev, [pendingEnquiryId!]: booking.csr_number }));
      // If a planner quote was linked, store the quote data in localStorage for the Stage Tracker to pick up
      if (linkedQuoteId) {
        const q = plannerQuotes.find(x => x.id === linkedQuoteId);
        if (q) {
          const pkg = (q.packageItems || [])[0];
          const linkedData = {
            bookingId: booking.id,
            quoteNumber: q.quoteNumber,
            customerName: q.customerName,
            customerPhone: q.customerPhone,
            customerEmail: q.customerEmail || '',
            quotedAmount: q.quotedAmount,
            serviceName: pkg?.serviceName || '',
            productName: pkg?.productName || '',
            qualityTier: pkg?.qualityTier || '',
            eventDates: pkg?.eventDates?.filter((d: any) => d.date).map((d: any) => d.date).join(', ') || '',
            addons: (pkg?.addOns || []).map((a: any) => a.name).join(', '),
          };
          const existing = JSON.parse(localStorage.getItem('ss_booking_quote_links') || '{}');
          existing[booking.id] = linkedData;
          localStorage.setItem('ss_booking_quote_links', JSON.stringify(existing));
        }
      }
      setShowBookingModal(false);
    } catch (e: any) {
      setError(e.message || 'Could not create booking');
    } finally {
      setBookingActionId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchEnquiries(activeService);
      setEnquiries(data);
    } catch (e: any) {
      setError(e.message || 'Could not load enquiries');
    } finally {
      setLoading(false);
    }
  }, [activeService]);

  useEffect(() => { if (connected) load(); else setLoading(false); }, [connected, load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (e: Enquiry) => {
    setEditing(e);
    setForm({
      customer_name: e.customer_name || '', contact_number: e.contact_number || '', email: e.email || '',
      contact_via: e.contact_via || CONTACT_VIA_OPTIONS[0], event_dates: e.event_dates || '',
      location_venue: e.location_venue || '', suggested_package: e.suggested_package || '',
      follow_up_date: e.follow_up_date || '', follow_up_status: e.follow_up_status || FOLLOW_UP_STATUS_OPTIONS[0],
      overall_status: e.overall_status, comments: e.comments || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (editing) {
        const updated = await updateEnquiry(editing.id, form);
        setEnquiries(prev => prev.map(en => en.id === updated.id ? updated : en));
      } else {
        const created = await createEnquiry({ service_type: activeService, ...form });
        setEnquiries(prev => [...prev, created]);
      }
      setShowForm(false);
    } catch (e: any) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!connected) {
    return (
      <div style={{ padding: 28, maxWidth: 700 }}>
        <h1>Enquiry Tracker</h1>
        <div className="alert alert-amber" style={{ marginTop: 16 }}>
          <WifiOff size={16} />
          <span>Not connected to the Lifecycle Management backend. This usually means it isn't deployed yet, or your session expired — try signing out and back in. Contact Shivam if this persists.</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 28, maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          {activeService && <h1>Enquiry Tracker — {activeService}</h1>}
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Wifi size={13} color="var(--teal)" /> Live · {enquiries.length} enquir{enquiries.length === 1 ? 'y' : 'ies'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
            <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={openNew}>
            <Plus size={15} /> New Enquiry
          </button>
        </div>
      </div>

      {/* Service tabs */}
      <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:16}}>
        {SERVICES.map(s=>(
          <button key={s} onClick={()=>{setActiveService(s);setBookingCreated({});}}
            style={{padding:'6px 12px',borderRadius:6,fontSize:12.5,fontWeight:activeService===s?700:400,cursor:'pointer',fontFamily:'inherit',
              background:activeService===s?'rgba(183,121,31,0.15)':'var(--bg-subtle)',
              color:activeService===s?'var(--gold)':'var(--text-secondary)',
              border:activeService===s?'1px solid rgba(183,121,31,0.3)':'1px solid transparent'}}>
            {s}
          </button>
        ))}
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          <AlertTriangle size={15} /> <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>Loading…</div>
      ) : enquiries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
          <p>No enquiries yet. Click "New Enquiry" to add the first one.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Ref No.</th><th>Customer</th><th>Contact</th><th>Via</th><th>Event Dates</th>
                <th>Follow-up Status</th><th>Overall Status</th><th>Booking</th><th>Updated By</th><th></th>
              </tr></thead>
              <tbody>
                {enquiries.map(e => (
                  <tr key={e.id}>
                    <td style={{ fontWeight: 600, color: 'var(--gold)' }}>{e.ref_number}</td>
                    <td>{e.customer_name || '—'}</td>
                    <td>{e.contact_number || '—'}</td>
                    <td>{e.contact_via || '—'}</td>
                    <td>{e.event_dates || '—'}</td>
                    <td>{e.follow_up_status || '—'}</td>
                    <td><span className={`badge ${STATUS_BADGE[e.overall_status] || 'badge-gold'}`}>{e.overall_status}</span></td>
                    <td>
                      {bookingCreated[e.id] ? (
                        <span style={{ fontSize: 12, color: 'var(--teal)', fontWeight: 600 }}>{bookingCreated[e.id]}</span>
                      ) : e.overall_status === 'Confirmed' ? (
                        <button className="btn btn-secondary btn-sm" disabled={bookingActionId === e.id} onClick={() => openBookingModal(e.id)}>
                          {bookingActionId === e.id ? 'Creating…' : 'Create Booking'}
                        </button>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{e.updated_by_name || '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {onBuildQuote && (
                        <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => onBuildQuote(e)} title="Build a quote for this enquiry">
                          Build Quote
                        </button>
                      )}
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openEdit(e)}><Pencil size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={() => !saving && setShowForm(false)}>
          <div className="card card-lg" style={{ width: 520, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="card-header">
              <h3>{editing ? `Edit Enquiry — ${editing.ref_number}` : `New ${activeService} Enquiry`}</h3>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowForm(false)}><X size={15} /></button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Customer Name</label>
                <input className="input" placeholder="Full name" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Contact Number</label>
                <input className="input" value={form.contact_number} onChange={e => setForm({ ...form, contact_number: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input className="input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Contact Via</label>
                <select className="select" value={form.contact_via} onChange={e => setForm({ ...form, contact_via: e.target.value })}>
                  {CONTACT_VIA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Event Dates</label>
                <input className="input" placeholder="e.g. March 12-15, 2027" value={form.event_dates} onChange={e => setForm({ ...form, event_dates: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Location / Venue</label>
                <input className="input" value={form.location_venue} onChange={e => setForm({ ...form, location_venue: e.target.value })} />
              </div>
            </div>
            <div className="form-group" style={{ position: 'relative' }}>
              <label>Suggested & Shortlisted Package</label>
              <input className="input" placeholder={catalogKey ? 'Type to search the Rate Catalog…' : 'No catalog for Any Event — type freely'}
                value={form.suggested_package}
                onChange={e => { setForm({ ...form, suggested_package: e.target.value }); setShowPackagePicker(true); }}
                onFocus={() => setShowPackagePicker(true)}
                onBlur={() => setTimeout(() => setShowPackagePicker(false), 150)} />
              {showPackagePicker && catalogKey && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:20, background:'#fff', border:'1px solid var(--border)',
                  borderRadius:8, marginTop:4, maxHeight:220, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.12)' }}>
                  {matchingProducts.length === 0 ? (
                    <div style={{ padding:'10px 14px', fontSize:12, color:'var(--text-muted)' }}>No matches — you can still type a custom package name.</div>
                  ) : matchingProducts.map(p => (
                    <div key={p.code} onMouseDown={() => { setForm({ ...form, suggested_package: `${p.productName} (${p.tier} · ${p.packageType})` }); setShowPackagePicker(false); }}
                      style={{ padding:'8px 14px', fontSize:12.5, cursor:'pointer', borderBottom:'1px solid var(--border)' }}
                      onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.background='var(--bg-subtle)'}
                      onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.background='#fff'}>
                      <div style={{ fontWeight:600 }}>{p.productName}</div>
                      <div style={{ color:'var(--text-muted)', fontSize:11 }}>{p.tier} · {p.packageType} · {p.code}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Follow-up Date</label>
                <input className="input" type="date" value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Follow-up Status</label>
                <select className="select" value={form.follow_up_status} onChange={e => setForm({ ...form, follow_up_status: e.target.value })}>
                  {FOLLOW_UP_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Overall Status</label>
              <select className="select" value={form.overall_status} onChange={e => setForm({ ...form, overall_status: e.target.value })}>
                {OVERALL_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Comments</label>
              <textarea className="textarea" value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })} />
            </div>

            {error && <div className="alert alert-error" style={{ marginBottom: 12 }}><AlertTriangle size={14} /> <span>{error}</span></div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Enquiry'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Booking creation modal */}
      {showBookingModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}}
          onClick={()=>!bookingActionId&&setShowBookingModal(false)}>
          <div className="card card-lg" style={{width:440,maxWidth:'90vw'}} onClick={e=>e.stopPropagation()}>
            <div className="card-header">
              <h3>Create Booking</h3>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setShowBookingModal(false)}><X size={15}/></button>
            </div>
            {/* Link to Planner Quote — the bridge between Rate Planner and Lifecycle */}
            <div style={{marginBottom:16,padding:'12px 14px',background:'var(--bg-subtle)',borderRadius:8,border:'1px solid var(--border)'}}>
              <div style={{fontWeight:600,fontSize:13,marginBottom:6}}>🔗 Link to Rate Planner Quote <span style={{fontWeight:400,fontSize:11,color:'var(--text-muted)'}}>(auto-fills refs and package details)</span></div>
              {autoLinkedNote && <div style={{fontSize:11,color:'var(--teal)',marginBottom:6}}>✓ Auto-linked — this quote was tagged to this enquiry from Quote History earlier.</div>}
              <select className="input" value={linkedQuoteId} onChange={e=>{setAutoLinkedNote(false); handleLinkQuote(e.target.value);}}>
                <option value="">— Select a quote (optional) —</option>
                {eligibleQuotes.map(q=>(
                  <option key={q.id} value={q.id}>
                    {q.quoteNumber} · {q.customerName} · ₹{(q.quotedAmount||0).toLocaleString('en-IN')} · {(q as any).documentType==='invoice'?'INVOICE':'QUOTATION'}
                  </option>
                ))}
              </select>
              {linkedQuoteId && (() => {
                const q=eligibleQuotes.find(x=>x.id===linkedQuoteId);
                if(!q) return null;
                const pkg=(q.packageItems||[])[0];
                return (
                  <div style={{marginTop:10,padding:'8px 12px',background:'rgba(56,178,172,0.08)',borderRadius:6,fontSize:12}}>
                    <div style={{fontWeight:600,color:'var(--teal)',marginBottom:4}}>✓ Quote details will be linked</div>
                    <div>Customer: <strong>{q.customerName}</strong> · {q.customerPhone}</div>
                    {pkg&&<div>Package: <strong>{pkg.serviceName} — {pkg.productName} ({pkg.qualityTier})</strong></div>}
                    {pkg?.eventDates?.length>0&&<div>Event Dates: {pkg.eventDates.filter((d:any)=>d.date).map((d:any)=>d.date).join(', ')}</div>}
                    <div>Quoted: <strong>₹{(q.quotedAmount||0).toLocaleString('en-IN')}</strong></div>
                  </div>
                );
              })()}
            </div>

            {/* Advance-received gate — SOP: advance actually received is what
                allows a booking to be created, not invoice creation alone. */}
            {linkedQuoteId ? (
              advanceOk ? (
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,padding:'10px 12px',background:'rgba(56,178,172,0.08)',border:'1.5px solid var(--teal)',borderRadius:8,fontSize:12,color:'var(--teal)'}}>
                  <Check size={14} /> <span>Advance confirmed received on this quote — booking can be created.</span>
                </div>
              ) : (
                <div className="alert alert-amber" style={{marginBottom:12,fontSize:12}}>
                  <AlertTriangle size={14} /> <span>This quote hasn't had its advance confirmed yet. Go to Quote History or Dashboard and use "Confirm Advance Received" first, then come back here.</span>
                </div>
              )
            ) : (
              <label style={{display:'flex',alignItems:'flex-start',gap:8,marginBottom:12,padding:'10px 12px',background:'var(--bg-subtle)',borderRadius:6,fontSize:12,cursor:'pointer'}}>
                <input type="checkbox" checked={manualAdvanceConfirmed} onChange={e=>setManualAdvanceConfirmed(e.target.checked)} style={{marginTop:2,cursor:'pointer'}} />
                <span>No quote linked, so there's nothing in the system to check automatically. I confirm the advance payment has actually been received for this booking.</span>
              </label>
            )}
            <div className="form-row">
              <div className="form-group">
                <label style={{fontSize:12,color:'var(--text-muted)'}}>System Quote Ref (auto)</label>
                <input className="input" value="Auto-generated on create" disabled style={{opacity:0.5}}/>
              </div>
              <div className="form-group">
                <label>Quote Ref <span style={{color:'var(--text-muted)',fontSize:11}}>(auto-filled if linked)</span></label>
                <input className="input" placeholder="e.g. SS-260720-1234" value={quoteRefStudio} onChange={e=>setQuoteRefStudio(e.target.value)}/>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label style={{fontSize:12,color:'var(--text-muted)'}}>System Invoice Ref (auto)</label>
                <input className="input" value="Auto-generated on create" disabled style={{opacity:0.5}}/>
              </div>
              <div className="form-group">
                <label>Invoice Ref <span style={{color:'var(--text-muted)',fontSize:11}}>(auto-filled if linked)</span></label>
                <input className="input" placeholder="e.g. SS-INV-26..." value={invoiceRefStudio} onChange={e=>setInvoiceRefStudio(e.target.value)}/>
              </div>
            </div>
            {error && <div className="alert alert-error" style={{marginBottom:12}}><AlertTriangle size={14}/> <span>{error}</span></div>}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:8}}>
              <button className="btn btn-secondary" onClick={()=>setShowBookingModal(false)} disabled={!!bookingActionId}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreateBooking} disabled={!!bookingActionId || !advanceOk} title={!advanceOk ? 'Confirm the advance is received before creating this booking' : undefined}>
                {bookingActionId?'Creating…':'Create Booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
